/**
 * Avatares default generados 100% localmente (Fase C del TODO de UI/UX):
 * SVGs inline codificados como data URI, sin depender de un CDN de
 * terceros (tipo DiceBear) en runtime. Si mas adelante se quiere mas
 * variedad, Fase F del TODO sube assets propios a Supabase Storage.
 *
 * Cada avatar es una forma geometrica simple sobre un color solido, con
 * paleta tomada de los primitivos ya definidos en tokens.css (no colores
 * nuevos inventados aca).
 */

interface DefaultAvatar {
  id: string
  dataUri: string
}

const AVATAR_SIZE = 64

function svgToDataUri(svg: string): string {
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

function circleAvatar(bg: string, shape: string): string {
  return svgToDataUri(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${AVATAR_SIZE} ${AVATAR_SIZE}">` +
      `<rect width="${AVATAR_SIZE}" height="${AVATAR_SIZE}" rx="${AVATAR_SIZE / 2}" fill="${bg}"/>` +
      shape +
      `</svg>`,
  )
}

const CENTER = AVATAR_SIZE / 2

export const DEFAULT_AVATARS: DefaultAvatar[] = [
  {
    id: "indigo-circle",
    dataUri: circleAvatar("#4338ca", `<circle cx="${CENTER}" cy="${CENTER}" r="16" fill="#e0e7ff"/>`),
  },
  {
    id: "green-square",
    dataUri: circleAvatar("#16a34a", `<rect x="20" y="20" width="24" height="24" rx="4" fill="#dcfce7"/>`),
  },
  {
    id: "yellow-triangle",
    dataUri: circleAvatar("#ca8a04", `<polygon points="32,18 46,44 18,44" fill="#fef9c3"/>`),
  },
  {
    id: "red-diamond",
    dataUri: circleAvatar("#dc2626", `<polygon points="32,16 48,32 32,48 16,32" fill="#fee2e2"/>`),
  },
  {
    id: "blue-hex",
    dataUri: circleAvatar(
      "#3b82f6",
      `<polygon points="32,16 45,23 45,41 32,48 19,41 19,23" fill="#dbeafe"/>`,
    ),
  },
  {
    id: "neutral-ring",
    dataUri: circleAvatar("#3f3f46", `<circle cx="${CENTER}" cy="${CENTER}" r="15" fill="none" stroke="#e4e4e7" stroke-width="5"/>`),
  },
  {
    id: "indigo-cross",
    dataUri: circleAvatar(
      "#6366f1",
      `<rect x="27" y="16" width="10" height="32" rx="2" fill="#e0e7ff"/><rect x="16" y="27" width="32" height="10" rx="2" fill="#e0e7ff"/>`,
    ),
  },
  {
    id: "green-dot-grid",
    dataUri: circleAvatar(
      "#22c55e",
      [
        [22, 22],
        [42, 22],
        [22, 42],
        [42, 42],
      ]
        .map(([cx, cy]) => `<circle cx="${cx}" cy="${cy}" r="5" fill="#dcfce7"/>`)
        .join(""),
    ),
  },
]

interface AvatarPickerProps {
  value: string
  onChange: (dataUri: string) => void
}

export function AvatarPicker({ value, onChange }: AvatarPickerProps) {
  return (
    <div
      role="group"
      aria-label="Elegir avatar predeterminado"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(48px, 1fr))",
        gap: "var(--space-2)",
        maxWidth: 320,
      }}
    >
      {DEFAULT_AVATARS.map((avatar) => {
        const selected = value === avatar.dataUri
        return (
          <button
            key={avatar.id}
            type="button"
            onClick={() => onChange(avatar.dataUri)}
            aria-label={`Usar avatar ${avatar.id}`}
            aria-pressed={selected}
            style={{
              padding: 2,
              borderRadius: "var(--radius-full)",
              border: selected ? "2px solid var(--brand-primary)" : "2px solid transparent",
              background: "transparent",
              cursor: "pointer",
              lineHeight: 0,
            }}
          >
            <img src={avatar.dataUri} alt="" width={40} height={40} style={{ borderRadius: "var(--radius-full)" }} />
          </button>
        )
      })}
    </div>
  )
}
